import { ApiCall, BookingEntry } from '../../../models/booking-flow/booking-entry.model';
import { Component, ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StateService } from '../../../services/state.service';
import { Router } from '@angular/router';
import { PHONE_PATTERN, POSTAL_CODE_PATTERN } from '../../shared/constants/regex-patterns';
import { ToastrService } from 'ngx-toastr';
import { map } from 'rxjs';
import { BookingType } from '../../../models/enums/booking-type.enum';

@Component({
  selector: 'booking-dispatcher',
  template: '',
  imports: [],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BookingDispatcher {
  #router = inject(Router);
  #stateService = inject(StateService);
  #destroyRef = inject(DestroyRef);
  #toastrService = inject(ToastrService);
  #destination: string = '/postal-code';

  private normalizeIncomingValue(value?: string): string | undefined {
    if (!value || value === 'null') {
      return undefined;
    }

    const trimmed = value.trim();
    return trimmed ? trimmed : undefined;
  }

  private parseIncomingValue(entry: BookingEntry): BookingEntry {
    if ('postalCode' in entry) {
      const postalCode = this.normalizeIncomingValue(entry.postalCode);
      if (postalCode) {
        const digits = postalCode.replace(/\D/g, '');
        entry.postalCode = digits
      }
    }
    return entry
  }

  private validateIncomingEntry(entry: BookingEntry): void {
    const errors: string[] = [];

    if ('postalCode' in entry) {
      const postalCode = this.normalizeIncomingValue(entry.postalCode);
      if (postalCode) {
        if (!POSTAL_CODE_PATTERN.test(postalCode)) {
          errors.push('Postnummer måste bestå av exakt 5 siffror.');
        }
      }
    }

    if ('phone' in entry) {
      const phone = this.normalizeIncomingValue(entry.phone);
      if (phone) {
        const digits = phone.replace(/\D/g, '');
        const canonical = digits
          .replace(/^46/, '')
          .replace(/^0/, '');

        if (!PHONE_PATTERN.test(canonical)) {
          errors.push('Telefonnummer måste bestå av 8 eller 9 siffror.');
        }
      }
    }

    if (errors.length > 0) {
      throw new Error(errors.join(' '));
    }
  }

  constructor() {
    this.#stateService.bookingParams$.pipe(
      map((entry) => {
        entry = this.parseIncomingValue(entry);
        this.validateIncomingEntry(entry);

        switch (entry.bookingType) {
          case BookingType.NewOpportunityCustomer:
          case BookingType.NewOpportunity: {
            this.#destination = '/postal-code'
            this.#stateService.booking.update(b => ({
              ...b,
              bookingType: entry.bookingType,
              meetingType: entry.meetingType,
              apiCallOnComplete: ApiCall.CREATE
            }))

              this.#stateService.customer.update(c => ({
              ...c,
              postalCode: entry.postalCode === null ? undefined : entry.postalCode,
            }))
            break;
          }
          case 'Reschedule': {
            this.#destination = '/create'
            this.#stateService.booking.update(b => ({
              ...b,
              bookingType: entry.bookingType,
              meetingType: entry.meetingType,
              location: { latitude: entry?.latitude ?? 0, longitude: entry?.longitude ?? 0, score: entry?.score ?? 0 },
              apiCallOnComplete: ApiCall.UPDATE
            }))

            this.#stateService.seller.update(s => ({
              ...s,
              userId: entry.userId,
            }))
            this.#stateService.extendedBooking.set({ eventId: entry.eventId })
            break;
          }
          case 'ExistingOpportunity':
          case 'Move': {
            this.#destination = '/create'

            this.#stateService.seller.update(s => ({
              ...s,
              email: entry.sellerEmail,
              userId: entry.bookingType === BookingType.ExistingOpportunity ? entry.userId : entry.userIdNew
            }))

            this.#stateService.customer.update(c => ({
              ...c,
              firstName: entry.firstName,
              lastName: entry.lastName,
              city: entry.city,
              postalCode: entry.postalCode,
              streetName: entry.streetName,
              streetNumber: entry.streetNumber,
              email: entry.customerEmail === 'null' ? undefined : entry.customerEmail,
              phone: entry.phone
            }))

            this.#stateService.booking.update(b => ({
              ...b,
              location: { latitude: entry?.latitude ?? 0, longitude: entry?.longitude ?? 0, score: entry?.score ?? 0 },
              bookingType: entry.bookingType,
              meetingType: entry.meetingType,
              opportunityId: entry.opportunityId,
              apiCallOnComplete: entry.bookingType === BookingType.ExistingOpportunity ? ApiCall.CREATE : ApiCall.MOVE,
              eventId: entry.bookingType === BookingType.Move ? entry.eventId : undefined,
              meetingId: entry.bookingType === BookingType.Move ? entry.meetingId : undefined,
              userIdOld: entry.bookingType === BookingType.Move ? entry.userIdOld : undefined
            }))

            if (entry.bookingType === BookingType.Move) {
              this.#stateService.extendedBooking.set({ userIdOld: entry.userIdOld, eventId: entry.eventId, meetingId: entry.meetingId })
            }

            break;
          }
          default:
            this.#destination = '/postal-code';
            break;
        }

        return entry
      }),
      takeUntilDestroyed(this.#destroyRef)
    ).subscribe({
      next: (entry: BookingEntry | null) => {
        this.#router.navigate([this.#destination], { state: { entry } });
      },
      error: (err) => {
        const message = String(err?.message ?? err);
        this.#toastrService.error(message, 'Ogiltig indata');
        this.#router.navigate(['/postal-code']);
      },
    });
  }
}
